import {isDev} from "./EnvironmentUtils";

export interface TypeCheckerFunction<T> {
    (content: unknown): content is T
}

class TypeChecker {
    has = <X extends {}, Y extends PropertyKey>(content: X, prop: Y | Y[], isOptional: boolean = false): content is X & Record<Y, unknown> => {
        let isPassed;

        if (Array.isArray(prop)) {
            const objectKeys = Object.keys(content);

            isPassed = prop.every((el) => objectKeys.includes(el as string));
        } else {
            isPassed = content.hasOwnProperty(prop);
        }

        this.error(isPassed || isOptional, content, `Object does not contain ${prop.toString()}`);
        return isPassed;
    };

    hasAll = <X extends {}, Y extends PropertyKey>(content: X, properties: Y[]): content is X & Record<Y, unknown> => {
        if (!this.isObjectPropertiesCount(content, properties.length)) {
            return false;
        }

        const isPassed = properties.every((el) => this.has(content, el));
        this.error(isPassed, content, 'collection of properties does not match');
        return isPassed;
    }

    isObjectPropertiesCount = <T extends object>(content: T, count: number) => {
        const isPassed = Object.keys(content).length === count;
        this.error(isPassed, content, 'Number of properties does not match');
        return isPassed;
    }

    isObject = (content: unknown): content is object => {
        const isPassed = typeof content === 'object' && content != null;
        this.error(isPassed, content, 'not an object');
        return isPassed;
    };

    isNull = (content: unknown): content is null => {
        const isPassed = this.checkIsNull(content);
        this.error(isPassed, content, 'not a null value');
        return isPassed;
    };

    isBoolean = (content: unknown): content is boolean => {
        const isPassed = this.checkIsBoolean(content);
        this.error(isPassed, content, 'not a boolean');
        return isPassed;
    };

    isNumber = (content: unknown): content is number => {
        const isPassed = this.checkIsNumber(content);
        this.error(isPassed, content, 'not a number value');
        return isPassed;
    };

    isNumberOrNull = (content: unknown): content is number | null => {
        const isNumber = this.checkIsNumber(content);
        const isNull = this.checkIsNull(content);

        this.error(isNumber || isNull, content, 'not a number nor null')

        return isNumber || isNull;
    };

    isString = (content: unknown): content is string => {
        const isPassed = this.checkIsString(content);
        this.error(isPassed, content, 'not a string');
        return isPassed;
    };

    isStringOrNull = (content: unknown): content is string | null => {
        const isString = this.checkIsString(content);
        const isNull = this.checkIsNull(content);

        this.error(isString || isNull, content, 'not a string nor null')

        return isString || isNull;
    };

    isStringOrNumber = (content: unknown): content is string | number => {
        const isNumber = this.checkIsNumber(content);
        const isString = this.checkIsString(content);

        this.error(isString || isNumber, content, 'not a string or number');

        return isString || isNumber;
    }

    isFunction = (content: unknown): content is Function => {
        const isPassed = typeof content === 'function';
        this.error(isPassed, content, 'not a function');
        return isPassed;
    }

    isArray = (content: unknown): content is Array<unknown> => {
        const isPassed = Array.isArray(content);
        this.error(isPassed, content, 'not an array');
        return isPassed;
    }

    isArrayOf = <T>(content: unknown, checker: TypeCheckerFunction<T>): content is T[] => {
        const isPassed = content != null && this.isArray(content) && content.every((el) => checker(el));
        this.error(isPassed, content, 'Is not an array of type');
        return isPassed;
    }

    isEnumValue = <T extends object>(content: unknown, source: T): content is T[keyof T] => {
        const isPassed = Object.values(source).includes(content as T[keyof T]);
        this.error(isPassed, content, 'Is not an Enum type');
        return isPassed;
    }

    isRecordsOf = <K extends string | number | symbol, T>(content: unknown, checker: TypeCheckerFunction<T>): content is Record<K, T> => {
        const isKey = (key: unknown): key is K => {
            return ['string', 'number', 'symbol'].includes(typeof key)
        };

        const isPassed = this.isObject(content)
            && Object.keys(content).every(isKey)
            && Object.values(content).every(checker);

        this.error(isPassed, content, 'Is not a record of type');
        return isPassed;
    }

    private checkIsNull = (content: unknown): content is null => {
        return typeof content === 'object' && content === null;
    };

    private checkIsBoolean = (content: unknown): content is boolean => {
        return typeof content === 'boolean';
    }

    private checkIsNumber = (content: unknown): content is number => {
        return typeof content === 'number' && !isNaN(content) && isFinite(content);
    }

    private checkIsString = (content: unknown): content is number => {
        return typeof content === 'string';
    }

    /**
     * Private developer's function to detect data with an error
     */
    private error = (isPassed: boolean, content: unknown, message: string = 'this value has failed') => {
        if (!isPassed && isDev()) {
            console.error(`Typecheck: ${message} :`, content);
        }
    }
}

const TypeCheck = new TypeChecker();

export default TypeCheck;












